
import { Link } from 'react-router-dom';

interface Booking {
  id: string;
  client_name: string;
  client_email: string;
  booking_date: string;
  booking_time: string;
  status: string;
  payment_status: string;
  currency: string;
  amount_paid: number;
}

interface RecentBookingsProps {
  bookings: Booking[];
}

export default function RecentBookings({ bookings }: RecentBookingsProps) {
  const recent = bookings.slice(0, 6);

  const formatDate = (date: string) =>
    new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${minutes} ${ampm}`;
  };

  const statusColor: Record<string, string> = {
    confirmed: 'bg-emerald-500',
    pending: 'bg-amber-500',
    cancelled: 'bg-red-400',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-bold text-slate-900">Recent Bookings</h3>
        <Link
          to="/admin?tab=bookings"
          className="text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors cursor-pointer"
        >
          View All
        </Link>
      </div>

      {recent.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-inbox-line text-xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500">No bookings yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {recent.map((b) => (
            <div
              key={b.id}
              className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-emerald-600">
                  {b.client_name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{b.client_name}</p>
                <p className="text-xs text-gray-500">
                  {formatDate(b.booking_date)} at {formatTime(b.booking_time)}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`w-2 h-2 rounded-full ${statusColor[b.status] || 'bg-gray-400'}`}></span>
                <span className="text-xs text-gray-500 capitalize">{b.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
